import { ResponsiveBar } from "@nivo/bar";
import React, {useContext} from "react";
import { BAR_CHART_DOT_COLORS } from "../../constants/Constants";
import "./ChartTooltip.css";
import CustomToolTipHost from "../../components/CustomToolTipHost/CustomToolTipHost";
import themeContext from "../../context/theme/ThemeContext";
import CustomTickWithUnit from "./CustomTickWithUnit";

const BarChartNivo = ({ data, keys, chartType, indexBy, yAxisText, totalCountForStudies, filterObjInChartComponent, handleBarClick, xAxisUnit, yAxisUnit }) => {
  const { getThemeColor,getAlterColor } = useContext(themeContext)
  const isPortraitMode = window.innerHeight > window.innerWidth && window.innerWidth >= 800;
  const getFormattedLabelForEditedOrReturnedOrRaisedAlertChart = (label) => {
    let formattedLabel = label.split("-")[0];
    return formattedLabel;
  }

  const tooltipStyle = {
    root: {display:'flex',alignItems:'center' },
  };
  
const finalKeys = React.useMemo(() => {
  if (chartType === "tat_analysis") {
    const orderedKeys = [];

    if (keys.includes("Top 25% TAT")) orderedKeys.push("Top 25% TAT");
    if (keys.includes("Mid 50% TAT")) orderedKeys.push("Mid 50% TAT");
    if (keys.includes("Bottom 25% TAT")) orderedKeys.push("Bottom 25% TAT");

    return orderedKeys;
  }
  return keys;
}, [chartType, keys]);

const NIVO_SET2 = ["#66c2a5","#fc8d62","#8da0cb","#e78ac3","#a6d854","#ffd92f","#e5c494","#b3b3b3"];

const getColorForKey = (chartType, key, index, data) => {
  if (chartType === "study_throughput") {
    const MODALITY_COLORS = { DX:"#4E79A7", CR:"#A0CBE8", CT:"#F28E2B", MR:"#FFBE7D", RG:"#59A14F", US:"#8CD17D", MG:"#B6992D", Others:"#E15759" };
    return MODALITY_COLORS[key] || "#ccc";
  }
  if (chartType === "tat_analysis") {
    const TAT_COLORS = { "Bottom 25% TAT":"#6be5e8", "Mid 50% TAT":"#40b9d4", "Top 25% TAT":"#2f89b5" };
    return TAT_COLORS[key] || "#ccc";
  }
  if (chartType === "ai_result" && data?.length > 0) {
    return data[0][`${key}Color`] || "#ccc";
  }
  return NIVO_SET2[index % NIVO_SET2.length];
};

const getBarColor = (chartType) => {
  if( chartType === "study_throughput") {
    // const MODALITY_COLORS = {
    //   DX: "#AF5902",
    //   CR: "#D36B03",
    //   CT: "#F67C03",
    //   MR: "#FB8E21",
    //   RG: "#FCA045",
    //   US: "#FCB268",
    //   MG: "#FDC48B",
    //   Others: "#FDCD8B",
    // };
    const MODALITY_COLORS = {
      DX: "#4E79A7",       // Cool blue
      CR: "#A0CBE8",       // Light sky blue
      CT: "#F28E2B",       // Warm orange
      MR: "#FFBE7D",       // Light orange
      RG: "#59A14F",       // Green
      US: "#8CD17D",       // Light green
      MG: "#B6992D",       // Mustard yellow
      Others: "#E15759",   // Coral red (to stand out)
    };

    return ({ id }) => MODALITY_COLORS[id] || "#ccc";
  }
  if (chartType === "tat_analysis") {
    const TAT_COLORS = {
      "Bottom 25% TAT": "#6be5e8",
      "Mid 50% TAT": "#40b9d4",
      "Top 25% TAT": "#2f89b5",
    };
    return ({ id }) => TAT_COLORS[id] || "#ccc";
  }

  if (chartType === "ai_result") {
    return ({ id, data }) => data[`${id}Color`] || "#ccc";
  }

  // fallback to Nivo's default color scheme
  return { scheme: "set2" };
};



  const orderedDates = data.map(d => d[indexBy]);

  // const shouldRotateTicks = (chartType === "ai_tat_analysis" || chartType === "study_throughput" || chartType === "ai_result" || chartType === "tat_analysis");
  const shouldRotateTicks = false;


  const barCount = data.length;
  // const dynamicPadding =
  //   barCount <= 3 ? 0.8 : // very thin bars for 1-3 bars
  //   barCount <= 6 ? 0.8 : // thinner bars for 4-6 bars
  //   0.3; 
    const isSingleBar = data.length === 1 && finalKeys.length === 1;
    const dynamicPadding =
      isSingleBar ? 0.7 :    // single bar -> ~30% width, centered
      barCount <= 3 ? 0.1 :  // wide bars
      barCount <= 6 ? 0.2 :  // medium-wide bars
      0.4;                   // narrow bars
                 
   
    const formatSecondsToMMSS = (seconds) => {
      const mm = Math.floor(seconds / 60);
      const ss = Math.floor(seconds % 60);
      return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    };

    // function formatDateToDDMMYYYY(dateStr) {
    //   const date = new Date(dateStr);
    //   if (isNaN(date.getTime())) return dateStr; // fallback for invalid date
    //   const dd = String(date.getDate()).padStart(2, '0');
    //   const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months start at 0
    //   const yyyy = date.getFullYear();
    //   return `${dd}-${mm}-${yyyy}`;
    // }

    const formatDateDDMMMYY = (value) => {
      const date = new Date(value);
      if (isNaN(date)) return value; // fallback if invalid date

      const day = String(date.getDate()).padStart(2, "0");
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = monthNames[date.getMonth()];
      const year = String(date.getFullYear()).slice(-2);

      return `${day}-${month}-${year}`;
    };

    const innerPadding =
       chartType === "tat_analysis" ? 5 : 0;

    const labelTextColor =
      chartType === "tat_analysis" ? "#ffffff" : getAlterColor("#ffffff", "#000");
    // Calculating tickValues for skipping every 2nd label
    const allDates = data.map((d) => d[indexBy]);
    const skip = data.length > 6 ? 2 : 1;
    const tickValues = allDates.filter((_, idx) => idx % skip === 0);

  return (
    <div style={{ display: "flex", height: "100%", width: "100%", position: "relative" }}>
     {yAxisUnit && (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "18px",
          transform: "translateY(-50%) rotate(-90deg)",
          transformOrigin: "center left",
          fontSize: "12px",
          fontWeight: "500",
          color: getAlterColor("#ffffff", "#000"),
          opacity: 0.9,
          whiteSpace: "nowrap",
          zIndex: 1,
          pointerEvents: "none",
        }}
      >
        {yAxisText && (
          <span style={{ fontSize: "12px", fontWeight: "bold" }}>{yAxisText}</span>
        )}
        {yAxisUnit && (
          <span style={{ fontSize: "10px"}}>
            &nbsp;({yAxisUnit})
          </span>
        )}
      </div>
    )}


    <div style={{ flex: 1, position: "relative",height: "100%", width: "100%" }}>
    <ResponsiveBar
      data={data}
      keys={finalKeys}
      indexBy={indexBy}
      margin={{ top: 40, right: isPortraitMode ? 20 : 150, bottom: isPortraitMode ? 50 : 80, left: 80 }}
      padding={dynamicPadding}
      groupMode={(chartType =="ai_result" || chartType == "edit_reports_count" || chartType=="returned_studies_count" || chartType=="raised_alert_count" || chartType=="study_throughput") ? "stacked" : "grouped"}
      valueScale={{ type: "linear" }}
      indexScale={{ type: "band", round: true, domain: orderedDates }}
      layout={(chartType == "edit_reports_count" || chartType=="returned_studies_count" || chartType=="raised_alert_count") ? "horizontal" : "vertical" }
      enableGridY = {( chartType == "edit_reports_count" || chartType=="returned_studies_count" || chartType=="raised_alert_count") ? false : true }
      enableGridX = {( chartType == "edit_reports_count" || chartType=="returned_studies_count" || chartType=="raised_alert_count") ? true : false }
      onClick={handleBarClick}
      innerPadding={innerPadding}
      theme={{
        background: "none",
        axis: {
          fontSize: "12px",
          domain: {
            line: {
              stroke: "#555555", // <-- X and Y axis line color
              strokeWidth: 1,
            },
          },
          tickColor: getAlterColor("#ffffff","#000"),
          ticks: {
            line: {
              stroke: "#555555",
            },
            text: {
              fill: getAlterColor("#ffffff","#000"),
              fontSize: 12,      // size of the numbers/dates
              fontWeight: 500 ,
            },
          },
          legend: {
            text: {
              fill: getAlterColor("#ffffff","#000"),
              fontSize: 12,             
              fontWeight: "bold",
            },
          },
        },
        grid: {
          line: {
            stroke: "#555555",
            strokeDasharray: "3 3",
          },
        },
        legends: {
          text: {
            fontSize: 13,
            fontFamily: "sans-serif",
            fill: getAlterColor("rgb(255,255,255,0.8)", "#000"),
          }
        }
      }}
      colors={getBarColor(chartType)}
      fill={[
        {
          match: {
            id: keys[0],
          },
          id: "first",
        },
        {
          match: {
            id: keys.length > 1 ? keys[1] : "",
          },
          id: "second",
        },
        {
          match: {
            id: keys.length > 2 ? keys[2] : "",
          },
          id: "third",
        },
      ]}
      borderColor={{
        from: "color",
        modifiers: [["darker", 1.6]],
      }}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: shouldRotateTicks ? 45 : 0,
        legend: chartType === "ai_result" ? `${indexBy}${xAxisUnit ? ` (${xAxisUnit})` : ""}` : "",
        legendPosition: "middle",
        legendOffset: shouldRotateTicks ? 70 : 40,
        tickValues: tickValues,
        format: e => {
          if (filterObjInChartComponent?.fullDay) {
              if (typeof e === "string") {
                const parts = e.split(" ");
                const timePart = parts.length > 1 ? parts[1] : e;

                if (timePart.includes(":")) {
                  const [hourStr] = timePart.split(":");

                  if (!isNaN(hourStr)) {
                    const hour = parseInt(hourStr, 10) || 0;
                    return `${String(hour +1).padStart(2, "0")}h`;
                  }
                }
                return e;
              }
            }else if (chartType === "ai_tat_analysis" || chartType === "study_throughput" || chartType === "tat_analysis"|| chartType === "ai_result") {
              return formatDateDDMMMYY(e);
            }
          return (chartType == "edit_reports_count" || chartType=="returned_studies_count" || chartType=="raised_alert_count") 
            ? Number.isInteger(e) ? e : '' 
            : null;
        }
      }}
      maxValue={(chartType == "edit_reports_count" || chartType=="returned_studies_count" || chartType=="raised_alert_count") ? totalCountForStudies == 0 ? 1 : 'auto' : 'auto'}
      axisLeft={{
        tickSize: 5,
        tickPadding: (chartType == "edit_reports_count" || chartType=="returned_studies_count" || chartType=="raised_alert_count") ? 2 : 5,
        tickRotation: 0,
        legend: yAxisUnit ? "" : yAxisText || "",
        legendPosition: "middle",
        legendOffset: (chartType == "edit_reports_count" || chartType=="returned_studies_count" || chartType=="raised_alert_count") ? -75 : -50,
        itemOpacity: 1,
        format: (e) => {
          if (chartType === "tat_analysis") {
            const mm = Math.floor(e / 60);
            const ss = Math.floor(e % 60);
            return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
          } else if (
            chartType === "edit_reports_count" ||
            chartType === "returned_studies_count" ||
            chartType === "raised_alert_count"
          ) {
            return typeof e === "string" && e.length > 10 ? e.substring(0, 10) + "..." : e;
          }
          return Number.isInteger(e) ? e : '';
        }
      }}
      labelSkipWidth={12}
      labelSkipHeight={0}
      label={(d) => (chartType === "tat_analysis" ? formatSecondsToMMSS(d.value) : d.value === 0 ? '' : d.value)}

      // labelTextColor={{
      //   from: "custom",
      //   modifiers: [["darker", 1.6]]
      // }}
      labelTextColor={getAlterColor("#ffffff","#000")}
      tooltip={(e) => {
        return (
          <div className="tooltip-container">
            <div className="tooltip-container_in">
              <div className="tooltip-container_in__inside">
                <span
                  style={{
                    display: "block",
                    width: "12px",
                    height: "12px",
                    background: e.color,
                    marginRight: "7px",
                  }}
                ></span>
                <span style={{ color: "black" }}>
               {(chartType === "edit_reports_count" || chartType === "returned_studies_count" || chartType === "raised_alert_count")
                  ? getFormattedLabelForEditedOrReturnedOrRaisedAlertChart(e.label)
                  : formatDateDDMMMYY(e.label)}
                  &nbsp;
                  <strong>{chartType === "tat_analysis" ? formatSecondsToMMSS(e.value) : e.value}</strong>
                </span>
              </div>
            </div>
          </div>
        );
      }}
      legends={(chartType == "edit_reports_count" || chartType=="returned_studies_count" || chartType=="raised_alert_count" || isPortraitMode) ? [] : [
        {
          dataFrom: "keys",
          anchor: "top-right",
          direction: "column",
          justify: false,
          translateX: 120,
          translateY: 0,
          itemsSpacing: 5,
          itemWidth: 100,
          itemHeight: 15,
          itemDirection: "left-to-right",
          itemTextColor: getAlterColor("rgb(255,255,255,0.8)","#000"),
          itemOpacity: 1,
          symbolSize: 15,
          effects: [
            {
              on: "hover",
              style: {
                itemOpacity: 10,
                itemTextColor:getAlterColor("#ffffff","#000"),
              },
            },
          ],
        },
      ]}
      role="application"
      barAriaLabel={(e) => e.id + ": " + e.formattedValue + e.indexValue}
    />
    
      {indexBy && chartType !== "ai_result" && (
        // <div style={{ textAlign: "center", marginTop: isPortraitMode ? "-10px" : "-35px", lineHeight: 1.4 }}>
        <div style={{ position: "absolute", left: 0, right: 0, bottom: isPortraitMode ? "6px" : "18px", textAlign: "center", lineHeight: 1.4 }}>
          <div style={{ fontSize: "12px", fontWeight: "bold" }}>
            {indexBy}
          </div>
          {xAxisUnit && (
            <div style={{ fontSize: "10px", fontWeight: "normal" }}>
              ({xAxisUnit})
            </div>
          )}
        </div>
      )}
      {isPortraitMode && !(chartType == "edit_reports_count" || chartType=="returned_studies_count" || chartType=="raised_alert_count") && (
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px 16px", padding: "6px 8px" }}>
          {finalKeys.map((key, index) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", color: getAlterColor("rgb(255,255,255,0.8)", "#000") }}>
              <div style={{ width: "12px", height: "12px", borderRadius: "2px", backgroundColor: getColorForKey(chartType, key, index, data), flexShrink: 0 }} />
              <span>{key}</span>
            </div>
          ))}
        </div>
      )}

      </div>
    </div>
  );
};

export default BarChartNivo;
